import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import Navbar from '../components/Navbar';
import { ChevronLeft, AlertTriangle, User, Building2, Calendar, CheckCircle, XCircle, Clock, X, FileText, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const INCIDENT_TYPES = {
  safety_concern: 'Safety Concern',
  criminal_charge: 'Criminal Charge',
  harassment: 'Harassment',
  policy_violation: 'Policy Violation',
  other: 'Other'
};

const STATUS_STYLES = {
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending Review' },
  reviewed: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Under Review' },
  resolved: { bg: 'bg-green-100', text: 'text-green-800', label: 'Resolved' }
};

const AdminIncidentReview = () => {
  const [loading, setLoading] = useState(true);
  const [incidents, setIncidents] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [resolution, setResolution] = useState('maintain');
  const [resolutionNotes, setResolutionNotes] = useState('');

  const fetchIncidents = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('incident_type', typeFilter);
      
      const response = await axios.get(`${API}/admin/incidents?${params.toString()}`);
      setIncidents(response.data);
    } catch (error) {
      toast.error('Failed to load incidents');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter]);

  useEffect(() => {
    fetchIncidents();
  }, [fetchIncidents]);

  const openResolveModal = (incident) => {
    setSelectedIncident(incident);
    setResolution('maintain');
    setResolutionNotes('');
    setShowResolveModal(true);
  };

  const handleResolve = async () => {
    try {
      await axios.put(`${API}/admin/incidents/${selectedIncident.id}/resolve`, {
        resolution,
        resolution_notes: resolutionNotes
      });
      
      const resolutionMsg = {
        maintain: 'Clearance maintained',
        suspend: 'Clearance suspended',
        revoke: 'Clearance revoked'
      }[resolution];
      
      toast.success(`Incident resolved: ${resolutionMsg}`);
      setShowResolveModal(false);
      fetchIncidents();
    } catch (error) {
      toast.error('Failed to resolve incident');
    }
  };

  const getStatusBadge = (status) => {
    const style = STATUS_STYLES[status] || STATUS_STYLES.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
        {style.label}
      </span>
    );
  };

  const getResolutionBadge = (resolution) => {
    const styles = {
      maintain: { bg: 'bg-green-100', text: 'text-green-800', label: 'Maintained' },
      suspend: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Suspended' },
      revoke: { bg: 'bg-red-100', text: 'text-red-800', label: 'Revoked' }
    };
    const style = styles[resolution];
    if (!style) return null;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
        {style.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen" data-testid="admin-incident-review">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <Link to="/admin" className="text-[#8B2E3C] hover:underline flex items-center gap-1 mb-4">
            <ChevronLeft className="w-4 h-4" />
            Back to Admin Dashboard
          </Link>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#222222] mb-2">Incident Review</h1>
          <p className="text-base text-[#666666]">Review and resolve volunteer incident reports</p>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-4 mb-6">
          <div className="card bg-yellow-50">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-yellow-600" />
              <div>
                <div className="text-2xl font-bold text-yellow-700">
                  {incidents.filter(i => i.status === 'pending').length}
                </div>
                <div className="text-sm text-yellow-600">Pending Review</div>
              </div>
            </div>
          </div>
          <div className="card bg-green-50">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div>
                <div className="text-2xl font-bold text-green-700">
                  {incidents.filter(i => i.status === 'resolved').length}
                </div>
                <div className="text-sm text-green-600">Resolved</div>
              </div>
            </div>
          </div>
          <div className="card bg-[#FBF4EA]">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-[#8B2E3C]" />
              <div>
                <div className="text-2xl font-bold text-[#8B2E3C]">
                  {incidents.length}
                </div>
                <div className="text-sm text-[#666666]">Total Reports</div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="card mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="input-field"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">Incident Type</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="input-field"
              >
                <option value="">All Types</option>
                {Object.entries(INCIDENT_TYPES).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Incidents List */}
        {loading ? (
          <div className="card text-center py-12">
            <div className="text-[#666666]">Loading incidents...</div>
          </div>
        ) : incidents.length === 0 ? (
          <div className="card text-center py-12">
            <AlertTriangle className="w-12 h-12 text-[#E9DDCF] mx-auto mb-4" />
            <p className="text-[#666666]">No incident reports found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {incidents.map((incident) => (
              <div key={incident.id} className="card">
                <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                  {/* Main Info */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-[#222222]">
                            {INCIDENT_TYPES[incident.incident_type] || incident.incident_type}
                          </span>
                          {getStatusBadge(incident.status)}
                          {incident.resolution && getResolutionBadge(incident.resolution)}
                        </div>
                        <div className="text-sm text-[#666666]">
                          Reported on {format(new Date(incident.created_at), 'MMM d, yyyy')}
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-3 mb-3">
                      <div className="flex items-center gap-2 text-sm">
                        <User className="w-4 h-4 text-[#8B2E3C]" />
                        <div>
                          <div className="font-medium">{incident.volunteer_name}</div>
                          <div className="text-[#666666]">{incident.volunteer_email}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Building2 className="w-4 h-4 text-[#8B2E3C]" />
                        <div>
                          <div className="font-medium">{incident.organization_name}</div>
                          <div className="text-[#666666]">Reported by {incident.reported_by_name}</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm mb-3">
                      <Calendar className="w-4 h-4 text-[#666666]" />
                      <span className="text-[#666666]">Incident date: {incident.date}</span>
                    </div>
                    
                    <div className="bg-[#FBF4EA] rounded-lg p-3">
                      <div className="text-sm font-medium text-[#222222] mb-1">Description</div>
                      <p className="text-sm text-[#666666]">{incident.description}</p>
                    </div>
                    
                    {incident.resolution_notes && (
                      <div className="mt-3 bg-green-50 rounded-lg p-3">
                        <div className="text-sm font-medium text-green-800 mb-1">Resolution Notes</div>
                        <p className="text-sm text-green-700">{incident.resolution_notes}</p>
                        <p className="text-xs text-green-600 mt-2">
                          Resolved by {incident.resolved_by} on {format(new Date(incident.resolved_at), 'MMM d, yyyy')}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Actions */}
                  {incident.status === 'pending' && (
                    <div className="lg:w-40 shrink-0">
                      <button
                        onClick={() => openResolveModal(incident)}
                        className="btn-primary w-full flex items-center justify-center gap-2"
                      >
                        <Shield className="w-4 h-4" />
                        Review
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Resolve Modal */}
      {showResolveModal && selectedIncident && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-[#222222]">Resolve Incident</h2>
                <button onClick={() => setShowResolveModal(false)} className="text-[#666666] hover:text-[#222222]">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="mb-4 p-3 bg-[#FBF4EA] rounded-lg">
                <div className="text-sm text-[#666666]">Volunteer</div>
                <div className="font-medium">{selectedIncident.volunteer_name}</div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Resolution *</label>
                  <div className="space-y-2">
                    <label className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:border-[#8B2E3C] transition-colors">
                      <input
                        type="radio"
                        name="resolution"
                        value="maintain"
                        checked={resolution === 'maintain'}
                        onChange={(e) => setResolution(e.target.value)}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium text-green-700">Maintain Clearance</div>
                        <div className="text-sm text-[#666666]">No action needed, clearance remains valid</div>
                      </div>
                    </label>
                    
                    <label className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:border-[#8B2E3C] transition-colors">
                      <input
                        type="radio"
                        name="resolution"
                        value="suspend"
                        checked={resolution === 'suspend'}
                        onChange={(e) => setResolution(e.target.value)}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium text-orange-700">Suspend Clearance</div>
                        <div className="text-sm text-[#666666]">Temporarily suspend pending further investigation</div>
                      </div>
                    </label>
                    
                    <label className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:border-[#8B2E3C] transition-colors">
                      <input
                        type="radio"
                        name="resolution"
                        value="revoke"
                        checked={resolution === 'revoke'}
                        onChange={(e) => setResolution(e.target.value)}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium text-red-700">Revoke Clearance</div>
                        <div className="text-sm text-[#666666]">Permanently revoke volunteer's clearance</div>
                      </div>
                    </label>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Resolution Notes</label>
                  <textarea
                    className="input-field min-h-[100px]"
                    value={resolutionNotes}
                    onChange={(e) => setResolutionNotes(e.target.value)}
                    placeholder="Provide notes about this resolution..."
                  />
                </div>
              </div>

              <div className="flex gap-3 justify-end mt-6">
                <button onClick={() => setShowResolveModal(false)} className="btn-outline">
                  Cancel
                </button>
                <button onClick={handleResolve} className="btn-primary">
                  Resolve Incident
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminIncidentReview;
